/**
 * Quick Shop Modal JavaScript
 * Handles modal opening, closing, and USP slider functionality
 */

class QuickShopModal {
  constructor() {
    this.modal = null;
    this.backdrop = null;
    this.closeButton = null;
    this.uspsSlider = null;
    this.isOpen = false;
    
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupModal());
    } else {
      this.setupModal();
    }
  }

  setupModal() {
    this.modal = document.querySelector('.quick-shop-modal');
    if (!this.modal) return;

    this.backdrop = this.modal.querySelector('[data-quick-shop-backdrop]');
    this.closeButton = this.modal.querySelector('[data-quick-shop-close]');
    this.uspsSlider = this.modal.querySelector('[data-quick-shop-usps-slider]');

    // Initially hide the modal
    this.modal.style.display = 'none';
    
    this.bindEvents();
    this.initUSPSlider();
    this.initExitIntent();
  }

  bindEvents() {
    // Close button
    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    // Backdrop click
    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => this.close());
    }

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Prevent body scroll when modal is open
    this.modal.addEventListener('transitionend', () => {
      if (this.isOpen) {
        document.body.classList.add('overflow-hidden');
      } else {
        document.body.classList.remove('overflow-hidden');
      }
    });

    // Consultation button click
    const consultationButton = this.modal.querySelector('.open-consultation-modal');
    if (consultationButton) {
      consultationButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.showConsultationForm();
      });
    }
  }

  initUSPSlider() {
    if (!this.uspsSlider || typeof Swiper === 'undefined') return;

    const swiper = new Swiper(this.uspsSlider, {
      slidesPerView: 'auto',
      spaceBetween: 1.2,
      centeredSlides: true,
      loop: true,
      autoplay: {
        delay: 3000,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      },
      speed: 800,
      a11y: {
        enabled: true,
        prevSlideMessage: 'Previous USP',
        nextSlideMessage: 'Next USP',
        firstSlideMessage: 'This is the first USP',
        lastSlideMessage: 'This is the last USP',
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      watchOverflow: true,
      centerInsufficientSlides: true,
    });

    // Pause autoplay when modal is closed
    this.modal.addEventListener('quick-shop-modal:close', () => {
      if (swiper.autoplay) {
        swiper.autoplay.stop();
      }
    });

    this.modal.addEventListener('quick-shop-modal:open', () => {
      if (swiper.autoplay) {
        swiper.autoplay.start();
      }
    });
  }

  open() {
    if (!this.modal || this.isOpen) return;

    this.isOpen = true;
    this.modal.style.display = 'flex';
    
    // Trigger reflow to ensure display change is applied
    this.modal.offsetHeight;
    
    this.modal.classList.add('show');
    
    // Focus management
    if (this.closeButton) {
      this.closeButton.focus();
    }
    
    // Emit custom event
    this.modal.dispatchEvent(new CustomEvent('quick-shop-modal:open', {
      bubbles: true,
      detail: { modal: this.modal }
    }));

    // Announce to screen readers
    this.announceToScreenReader('Quick shop modal opened');
  }

  close() {
    if (!this.modal || !this.isOpen) return;

    this.isOpen = false;
    this.modal.classList.remove('show');
    
    // Hide consultation form if it's showing
    this.hideConsultationForm();
    
    // Remove from display after animation
    setTimeout(() => {
      if (!this.isOpen) {
        this.modal.style.display = 'none';
        document.body.classList.remove('overflow-hidden');
        // Set cookie that expires in 30 days
        this.setExitIntentCookie();
      }
    }, 300);

    // Emit custom event
    this.modal.dispatchEvent(new CustomEvent('quick-shop-modal:close', {
      bubbles: true,
      detail: { modal: this.modal }
    }));

    // Announce to screen readers
    this.announceToScreenReader('Quick shop modal closed');
  }

  announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'visually-hidden';
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  showConsultationForm() {
    const consultationForm = this.modal.querySelector('.quick-shop-modal_free_cunsult_form');
    const modalContent = this.modal.querySelector('.quick-shop-modal__body');
    
    if (consultationForm ) {
      // Hide the main content
      modalContent.style.display = 'none';
      
      // Show the consultation form
      consultationForm.style.display = 'flex';
      
     
    }
  }

  hideConsultationForm() {
    const consultationForm = this.modal.querySelector('.quick-shop-modal_free_cunsult_form');
    const modalContent = this.modal.querySelector('.quick-shop-modal__body');
    
    if (consultationForm && modalContent) {
      // Hide the consultation form
      consultationForm.style.display = 'none';
      
      // Show the main content
      modalContent.style.display = "flex";
      
     
    }
  }

  setExitIntentCookie() {
    // Cookie helper function to set exitIntentShown cookie with 30-day expiration
    const date = new Date();
    date.setTime(date.getTime() + (30 * 24 * 60 * 60 * 1000)); // 30 days
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `exitIntentShown=true;${expires};path=/`;
  }

  // Cookie helper functions
  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  }

  setCookie(name, value, days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/`;
  }

  initExitIntent() {
    // Exit intent detection and show popup once every 30 days
    const showExitIntentPopup = () => {
      // Check if quick shop modal exists and open it
      if (this.modal) {
        this.open();
        // Set cookie that expires in 30 days
        this.setCookie('exitIntentShown', 'true', 30);
      }
    };

    // Only show if cookie doesn't exist (not shown in last 30 days)
    if (!this.getCookie('exitIntentShown')) {
      document.addEventListener('mouseout', (e) => {
        // Only trigger if leaving the document upwards
        if (
          e.relatedTarget === null &&
          e.clientY <= 0 // Mouse leaves at top edge
        ) {
          // Double check cookie before showing (prevents multiple triggers)
          if (!this.getCookie('exitIntentShown')) {
            showExitIntentPopup();
          }
        }
      });
    }
  }

  // Public API
  get isModalOpen() {
    return this.isOpen;
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.quickShopModal = new QuickShopModal();
});

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = QuickShopModal;
}
